import React from 'react';
import { cellKey, resolveCell, shadowsFor } from '../../../util/templateOverrides';
import { priceText } from './catalogValidation';
import './serviceCatalog.css';

/**
 * PriceCell — one per-template absolute price override cell.
 *
 * Originally the cell primitive of a full-page override matrix
 * (`TemplatePricingGrid` + `TemplateEditView`, both since removed — eight
 * price cells per service fit inside `TemplateModal`'s existing service
 * card, so the page was deleted rather than kept as a second editing
 * surface). This is the part of that build that carries over: inherit /
 * override / disabled semantics, the shadow marker, the unit suffix, and a
 * `stopPropagation`-safe input. `TemplateModal` renders it directly inside
 * its own per-service `<table>`.
 *
 * The server resolves COALESCE(template, trim, base); this component is only
 * about authoring the template layer. See `util/templateOverrides.js` for the
 * semantics (absent = inherit, 0 = a real price) that every cell here must
 * preserve.
 *
 * Props:
 *   svc           { id, name }  the master service this cell belongs to —
 *                 `id` is the master_service_id, used for the cell key.
 *   size          'S' | 'M' | 'L' | 'XL' (or a caller-defined single-rate key)
 *   catalog       'RETAIL' | 'WHOLE'
 *   base          number | null | undefined  the resolved base price;
 *                 undefined/null means no base row exists — the cell disables.
 *   overrides     Map<cellKey, number>  current override layer
 *   shadowIndex   Map  from indexTrimShadows — which cells shadow a
 *                 vehicle-specific trim price
 *   onChange      (nextMap) => void  called with a full replacement Map on
 *                 every edit
 *   readOnly      bool
 *   colSpan       number  optional, for a single-rate row spanning size columns
 *   unitLabel     string  optional, suffixes the rate (`/sq ft`, `/ft`)
 *
 * Renders a `<td>` — callers must place it directly inside a `<tr>`. It does
 * NOT stop click propagation itself (its original host row had no click
 * handler); a caller whose row IS itself a click target (TemplateModal's
 * service card toggles selection on click) must wrap it so a click reaching
 * the input doesn't also fire that handler.
 */
export function PriceCell({ svc, size, catalog, base, overrides, shadowIndex, onChange, readOnly, colSpan, unitLabel }) {
  const key = cellKey(svc.id, size, catalog);
  const { value, isOverride } = resolveCell(base, overrides.get(key));
  const shadows = shadowsFor(shadowIndex, svc.id, size);
  const label = `${svc.name} ${catalog === 'RETAIL' ? 'Retail' : 'Wholesale'} ${size}`;

  // No base row means the server has nothing to resolve against: both catalog
  // reads start FROM installer_services, so an override here is unreachable
  // (spec D11). Marine seeded RETAIL-first is the live case.
  if (base === null || base === undefined) {
    return <td className="tpl-cell tpl-cell--disabled" colSpan={colSpan}>no base price</td>;
  }

  const commit = (raw) => {
    const next = new Map(overrides);
    const trimmed = String(raw).trim();
    // Empty clears the override. NEVER write 0 or null to mean "inherit" —
    // absent row is inherit, and 0 is a price someone may genuinely want.
    if (trimmed === '') next.delete(key);
    else {
      const n = Number(trimmed);
      // Reject negatives here — `min="0"` on the input is advisory only and
      // does not stop typing, and a negative has no business reaching a
      // DECIMAL(10,2) override column.
      if (Number.isFinite(n) && n >= 0) next.set(key, n);
    }
    onChange(next);
  };

  const hasShadow = shadows.length > 0;

  return (
    <td
      className={`tpl-cell ${isOverride ? 'tpl-cell--override' : 'tpl-cell--inherited'} ${hasShadow ? 'tpl-cell--shadowed' : ''}`}
      colSpan={colSpan}
    >
      <div className="tpl-cell__inner">
        <input
          type="number" step="0.01" min="0" disabled={readOnly}
          value={isOverride ? value : ''}
          placeholder={priceText(base)}
          aria-label={label}
          onChange={(e) => commit(e.target.value)}
        />
        {unitLabel && <span className="tpl-cell__unit">/{unitLabel}</span>}
        {hasShadow && (
          <i
            className="fa-sharp fa-solid fa-triangle-exclamation tpl-cell__shadow"
            title={`Overrides ${shadows.length} vehicle-specific price${shadows.length === 1 ? '' : 's'} for dealerships on this template: ${shadows.map((s) => s.label).join(', ')}`}
          />
        )}
      </div>
    </td>
  );
}

export default PriceCell;
